/**
 * LabelPicker - Component for selecting and managing labels on tasks
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Tag, Plus, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Input } from '@components/ui/input';
import Badge from '@components/ui/Badge';
import type { Label } from '@services/api';

// Default color for labels
const DEFAULT_LABEL_COLOR = '#6B7280';

// Predefined color palette for labels
const LABEL_COLORS = [
  '#EF4444', // red
  '#F97316', // orange
  '#F59E0B', // amber
  '#EAB308', // yellow
  '#84CC16', // lime
  '#22C55E', // green
  '#14B8A6', // teal
  '#06B6D4', // cyan
  '#0EA5E9', // sky
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#A855F7', // purple
  '#D946EF', // fuchsia
  '#EC4899', // pink
  '#6B7280', // gray
];

interface LabelPickerProps {
  /** Currently selected labels */
  selectedLabels: Label[];
  /** All available labels for the project */
  availableLabels: Label[];
  /** Called when a label is added to the task */
  onAddLabel: (labelId: string) => void;
  /** Called when a label is removed from the task */
  onRemoveLabel: (labelId: string) => void;
  /** Called when a new label is created */
  onCreateLabel?: (data: { name: string; color: string }) => Promise<Label>;
  /** Whether to show as compact (icon only) or full button */
  compact?: boolean;
  /** Whether the picker is disabled */
  disabled?: boolean;
}

export default function LabelPicker({
  selectedLabels,
  availableLabels,
  onAddLabel,
  onRemoveLabel,
  onCreateLabel,
  compact = false,
  disabled = false,
}: LabelPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(LABEL_COLORS[0] ?? DEFAULT_LABEL_COLOR);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when popover closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setIsCreating(false);
      setNewLabelName('');
      setNewLabelColor(LABEL_COLORS[0] ?? DEFAULT_LABEL_COLOR);
    }
  }, [isOpen]);

  // Filter labels based on search
  const filteredLabels = availableLabels.filter((label) =>
    label.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if a label is selected
  const isSelected = (labelId: string) =>
    selectedLabels.some((l) => l.id === labelId);

  const handleToggleLabel = (label: Label) => {
    if (isSelected(label.id)) {
      onRemoveLabel(label.id);
    } else {
      onAddLabel(label.id);
    }
  };

  const handleCreateLabel = async () => {
    if (!newLabelName.trim() || !onCreateLabel) return;

    setIsSubmitting(true);
    try {
      const newLabel = await onCreateLabel({
        name: newLabelName.trim(),
        color: newLabelColor,
      });
      // Automatically add the new label to the task
      onAddLabel(newLabel.id);
      setIsCreating(false);
      setNewLabelName('');
      setNewLabelColor(LABEL_COLORS[0] ?? DEFAULT_LABEL_COLOR);
    } catch (error) {
      console.error('Failed to create label:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={clsx(
            'flex items-center gap-1.5 rounded-lg transition-colors',
            'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
            'dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white',
            'disabled:cursor-not-allowed disabled:opacity-50',
            compact ? 'p-1.5' : 'px-2 py-1.5 text-sm'
          )}
        >
          <Tag className="h-4 w-4" />
          {!compact && (
            <span>
              {selectedLabels.length > 0
                ? t('task.labelCount', { count: selectedLabels.length })
                : t('task.addLabels')}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent className="z-[150] w-72 p-0" align="start">
        <div className="p-3 border-b border-neutral-200 dark:border-neutral-700">
          <h4 className="font-medium text-sm text-neutral-900 dark:text-white mb-2">
            {t('task.labels')}
          </h4>
          <Input
            type="text"
            placeholder={t('task.searchLabels')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-sm"
          />
        </div>

        {/* Selected labels */}
        {selectedLabels.length > 0 && (
          <div className="p-2 border-b border-neutral-200 dark:border-neutral-700">
            <div className="flex flex-wrap gap-1">
              {selectedLabels.map((label) => (
                <Badge
                  key={label.id}
                  color={label.color}
                  size="sm"
                  onRemove={() => onRemoveLabel(label.id)}
                >
                  {label.name}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Available labels */}
        <div className="max-h-48 overflow-y-auto">
          {filteredLabels.length > 0 ? (
            <div className="p-1">
              {filteredLabels.map((label) => (
                <button
                  key={label.id}
                  type="button"
                  onClick={() => handleToggleLabel(label)}
                  className={clsx(
                    'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                    'hover:bg-neutral-100 dark:hover:bg-neutral-700',
                    'transition-colors'
                  )}
                >
                  <span
                    className="h-3 w-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: label.color }}
                  />
                  <span className="flex-1 text-left text-neutral-900 dark:text-white truncate">
                    {label.name}
                  </span>
                  {isSelected(label.id) && (
                    <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  )}
                </button>
              ))}
            </div>
          ) : searchQuery ? (
            <div className="p-3 text-center text-sm text-neutral-500">
              {t('task.noLabelsFound')}
            </div>
          ) : (
            <div className="p-3 text-center text-sm text-neutral-500">
              {t('task.noLabelsAvailable')}
            </div>
          )}
        </div>

        {/* Create new label */}
        {onCreateLabel && (
          <div className="border-t border-neutral-200 dark:border-neutral-700 p-2">
            {isCreating ? (
              <div className="space-y-2">
                <Input
                  type="text"
                  placeholder={t('task.labelNamePlaceholder')}
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleCreateLabel();
                    }
                    if (e.key === 'Escape') {
                      setIsCreating(false);
                    }
                  }}
                  className="h-8 text-sm"
                  autoFocus
                />
                <div className="flex flex-wrap gap-1">
                  {LABEL_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewLabelColor(color)}
                      className={clsx(
                        'h-5 w-5 rounded-full transition-transform',
                        newLabelColor === color && 'ring-2 ring-offset-1 ring-neutral-900 dark:ring-white scale-110'
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsCreating(false)}
                    className="flex-1 rounded-md px-2 py-1 text-sm text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-700"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateLabel}
                    disabled={!newLabelName.trim() || isSubmitting}
                    className={clsx(
                      'flex-1 rounded-md px-2 py-1 text-sm font-medium',
                      'bg-primary-600 text-white hover:bg-primary-700',
                      'disabled:cursor-not-allowed disabled:opacity-50'
                    )}
                  >
                    {isSubmitting ? t('common.creating') : t('common.create')}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsCreating(true)}
                className={clsx(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                  'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900',
                  'dark:text-neutral-400 dark:hover:bg-neutral-700 dark:hover:text-white'
                )}
              >
                <Plus className="h-4 w-4" />
                {t('task.createNewLabel')}
              </button>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface LabelDisplayProps {
  labels: Label[];
  onRemove?: (labelId: string) => void;
  maxVisible?: number;
  size?: 'sm' | 'md';
}

/**
 * LabelDisplay - Displays a list of labels with optional remove functionality
 */
export function LabelDisplay({
  labels,
  onRemove,
  maxVisible = 3,
  size = 'sm',
}: LabelDisplayProps) {
  if (labels.length === 0) return null;

  const visibleLabels = labels.slice(0, maxVisible);
  const hiddenCount = labels.length - maxVisible;

  return (
    <div className="flex flex-wrap gap-1">
      {visibleLabels.map((label) => (
        <Badge
          key={label.id}
          color={label.color}
          size={size}
          {...(onRemove && { onRemove: () => onRemove(label.id) })}
        >
          {label.name}
        </Badge>
      ))}
      {hiddenCount > 0 && (
        <Badge size={size} variant="subtle">
          +{hiddenCount}
        </Badge>
      )}
    </div>
  );
}
